
import { useNavigate } from 'react-router-dom';
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from 'react-redux';
import { RootState } from "../../state/store.ts";
import { AgentHeader } from '../../components/ui/agents/header';
import { ProfileCard } from '../../components/ui/agents/profile';
import {
    User,
    Calendar,
    Settings,
    LogIn,
    LogOut,
    CreditCard,
    Link as LinkIcon,
    CheckCircle2,
    AlertTriangle,
    ChevronLeft,
    ChevronRight,
    PhoneIncoming,
    Clock4,
    Wallet,
} from "lucide-react";
import axios from 'axios';
import Appointments from '../../components/ui/agents/appointments.tsx';
import { CallModal } from '../../components/ui/agents/callmodal.jsx';
import { formatPriceNOK, parseUntilObject } from '../../utilities/conversions.js';
import { useQuery } from '@tanstack/react-query';
import { current } from '@reduxjs/toolkit';
import Swal from 'sweetalert2';
import { BroadcastModal } from '../../components/ui/agents/broadcastmodal.jsx';
import PerformanceAndKpi from '../../components/ui/agents/performanceandkpi.tsx';
import Invoices from '../../components/ui/agents/invoices.tsx';
import { Toggle } from '../../components/ui/toggle.tsx';


/**
* Veileder – Kontrollpanel (Norsk)
* Frontend‑mock uten backend (state i minne/localStorage).
* Innhold: Login (mock), oversikt, avtaler, kalender/tilgjengelighet (+ mini‑måned),
* utbetalinger, meldinger, profil, innstillinger, Stripe/Vipps, mottakstoggles,
* prising per minutt eller per konsultasjon.
*/

/**********************
 * Små‑hjelpere
 **********************/
const NOK = (n: number) =>
    new Intl.NumberFormat("nb-NO", { style: "currency", currency: "NOK", maximumFractionDigits: 0 }).format(n);
const todayISO = () => new Date().toISOString().slice(0, 10);
const DAYS = ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"] as const;
const WEEKDAY_NAMES = ["Søn", "Man", "Tir", "Ons", "Tor", "Fre", "Lør"] as const; // Date.getDay()
const card = "rounded-2xl bg-white border border-purple-100 p-5 shadow";
function ymdLocal(d: Date) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const da = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${da}`;
}
function cls(...xs: Array<string | false | undefined>) { return xs.filter(Boolean).join(" "); }

/**********************
 * Auth (mock)
 **********************/
// function useAuth() {
//     const [user, setUser] = useState<null | { id: string; name: string; phone: string }>(() => {
//         try {
//             const raw = localStorage.getItem("advisorUser");
//             return raw ? JSON.parse(raw) : null;
//         } catch { return null; }
//     });
//     function login(name: string, phone: string) {
//         const id = (globalThis.crypto && "randomUUID" in globalThis.crypto)
//             ? crypto.randomUUID()
//             : Math.random().toString(36).slice(2);
//         const u = { id, name, phone };
//         setUser(u);
//         try { localStorage.setItem("advisorUser", JSON.stringify(u)); } catch { }
//     }
//     function logout() {
//         setUser(null);
//         try { localStorage.removeItem("advisorUser"); } catch { }
//     }
//     return { user, login, logout };
// }

/**********************
 * Kalender‑hjelpere
 **********************/
function genTimes(startHour = 7, endHour = 22, stepMinutes = 60) {
    const out: string[] = [];
    const totalMin = (endHour - startHour) * 60;
    for (let m = 0; m < totalMin; m += stepMinutes) {
        const abs = startHour * 60 + m;
        const hh = Math.floor(abs / 60);
        const mm = abs % 60;
        out.push(String(hh).padStart(2, "0") + ":" + String(mm).padStart(2, "0"));
    }
    return out;
}
function toHour(v: string, fallback: number) {
    const parts = (v || "").split(":");
    const n = parseInt(parts[0] || "", 10);
    if (Number.isNaN(n)) return fallback;
    return Math.max(0, Math.min(23, n));
}
function applyHoursToDays(
    base: Record<(typeof DAYS)[number], Record<number, boolean>>,
    opts: { from: number; to: number; brFrom?: number; brTo?: number; days: Record<string, boolean> }
) {
    const { from, to, brFrom, brTo, days } = opts;
    const next: Record<(typeof DAYS)[number], Record<number, boolean>> = { ...base };
    (Object.keys(days) as Array<keyof typeof days>).forEach((d) => {
        if (!days[d as string]) return;
        const col: Record<number, boolean> = { ...(next as any)[d as string] };
        for (let h = 7; h <= 22; h++) {
            let on = h >= from && h < to;
            if (on && brFrom != null && brTo != null) {
                if (h >= (brFrom as number) && h < (brTo as number)) on = false;
            }
            col[h] = on;
        }
        (next as any)[d as string] = col;
    });
    return next;
}

/**********************
 * QuickPlanPanel – arbeidstid/pauser + slots
 **********************/
function QuickPlanPanel({
    availability,
    setAvailability,
    slotMinutes,
    setSlotMinutes,
}: {
    availability: Record<(typeof DAYS)[number], Record<number, boolean>>;
    setAvailability: (v: any) => void;
    slotMinutes: number;
    setSlotMinutes: (n: number) => void;
}) {
    const [from, setFrom] = useState("09:00");
    const [to, setTo] = useState("16:00");
    const [brFrom, setBrFrom] = useState("12:00");
    const [brTo, setBrTo] = useState("13:00");
    const [mode, setMode] = useState<'weekly' | 'monthly'>(() => {
        try { return (localStorage.getItem('advisorPlanMode') as 'weekly' | 'monthly') || 'weekly'; } catch { return 'weekly'; }
    });
    const [days, setDays] = useState<Record<string, boolean>>({ Man: true, Tir: true, Ons: true, Tor: true, Fre: true, Lør: false, Søn: false });

    useEffect(() => { try { localStorage.setItem('advisorPlanMode', mode); } catch { } }, [mode]);

    function pickPreset(a: number, b: number, brA?: number, brB?: number) {
        setFrom(String(a).padStart(2, '0') + ":00");
        setTo(String(b).padStart(2, '0') + ":00");
        if (brA != null) setBrFrom(String(brA).padStart(2, '0') + ":00");
        if (brB != null) setBrTo(String(brB).padStart(2, '0') + ":00");
    }
    function applyPlan() {
        const f = toHour(from, 9);
        const t = toHour(to, 16);
        const bf = toHour(brFrom, 12);
        const bt = toHour(brTo, 13);
        const safeFrom = Math.min(f, t);
        const safeTo = Math.max(f, t);
        const next = applyHoursToDays(availability, { from: safeFrom, to: safeTo, brFrom: bf, brTo: bt, days });
        setAvailability(next);
    }
    function clearDays() {
        const next = applyHoursToDays(availability, { from: 0, to: 0, days, brFrom: undefined, brTo: undefined });
        setAvailability(next);
    }
    function toggleDay(d: string) { setDays(prev => ({ ...prev, [d]: !prev[d] })); }

    return (
        <div className="rounded-xl border border-purple-100 p-3 bg-gradient-to-br from-white to-purple-50">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:gap-4">
                <div>
                    <label className="block text-xs text-gray-600">Arbeidstid fra</label>
                    <input type="time" step={3600} value={from} onChange={e => setFrom(e.target.value)} className="rounded-lg border px-2 py-1" />
                </div>
                <div>
                    <label className="block text-xs text-gray-600">Arbeidstid til</label>
                    <input type="time" step={3600} value={to} onChange={e => setTo(e.target.value)} className="rounded-lg border px-2 py-1" />
                </div>
                <div>
                    <label className="block text-xs text-gray-600">Pause fra</label>
                    <input type="time" step={3600} value={brFrom} onChange={e => setBrFrom(e.target.value)} className="rounded-lg border px-2 py-1" />
                </div>
                <div>
                    <label className="block text-xs text-gray-600">Pause til</label>
                    <input type="time" step={3600} value={brTo} onChange={e => setBrTo(e.target.value)} className="rounded-lg border px-2 py-1" />
                </div>
                <div>
                    <label className="block text-xs text-gray-600">Slots (min)</label>
                    <div className="flex gap-2">
                        {[15, 30, 45, 60].map(m => (
                            <button key={m} onClick={() => setSlotMinutes(m)} className={cls("rounded-full border px-3 py-1 text-sm", slotMinutes === m ? "bg-purple-600 text-white border-purple-600" : "bg-white text-gray-700 border-gray-300")}>{m}</button>
                        ))}
                    </div>
                </div>
                <div>
                    <label className="block text-xs text-gray-600">Frekvens</label>
                    <div className="flex gap-3 text-sm">
                        <label className="inline-flex items-center gap-1"><input type="radio" name="mode" checked={mode === 'weekly'} onChange={() => setMode('weekly')} /> Ukentlig</label>
                        <label className="inline-flex items-center gap-1"><input type="radio" name="mode" checked={mode === 'monthly'} onChange={() => setMode('monthly')} /> Månedlig</label>
                    </div>
                </div>
                <div className="md:ml-auto flex gap-2">
                    <button onClick={applyPlan} className="rounded-full bg-purple-600 text-white px-4 py-2 text-sm">Bruk på valgte dager</button>
                    <button onClick={clearDays} className="rounded-full bg-gray-200 text-gray-800 px-4 py-2 text-sm">Tøm på valgte dager</button>
                </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                {DAYS.map(d => (
                    <button key={d} onClick={() => toggleDay(d)} className={cls("rounded-full border px-3 py-1", days[d] ? "bg-purple-600 text-white border-purple-600" : "bg-white text-gray-700 border-gray-300")}>{d}</button>
                ))}
                <div className="ml-auto flex items-center gap-2">
                    <span className="text-xs text-gray-600">Hurtigvalg:</span>
                    <button onClick={() => pickPreset(9, 16, 12, 13)} className="rounded-full border px-3 py-1">09–16 (12–13)</button>
                    <button onClick={() => pickPreset(10, 18, 13, 14)} className="rounded-full border px-3 py-1">10–18 (13–14)</button>
                    <button onClick={() => pickPreset(12, 20, 16, 17)} className="rounded-full border px-3 py-1">12–20 (16–17)</button>
                </div>
            </div>
        </div>
    );
}

/**********************
 * Mini månedsoversikt
 **********************/
function MiniMonth({ value, onChange }: { value: Date; onChange: (d: Date) => void }) {
    const [cursor, setCursor] = useState<Date>(() => new Date(value));
    useEffect(() => { setCursor(new Date(value)); }, [value]);

    const monthLabel = new Intl.DateTimeFormat('nb-NO', { month: 'long', year: 'numeric' }).format(cursor);

    function addMonths(base: Date, delta: number) { const d = new Date(base); d.setMonth(d.getMonth() + delta); return d; }
    function startOfMonth(d: Date) { return new Date(d.getFullYear(), d.getMonth(), 1); }
    function daysInMonth(d: Date) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); }
    function isSameDay(a: Date, b: Date) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }

    const first = startOfMonth(cursor);
    const leading = first.getDay(); // 0=Sun..6=Sat
    const total = daysInMonth(cursor);
    const cells: Array<{ date: Date | null, key: string }> = [];
    for (let i = 0; i < leading; i++) cells.push({ date: null, key: `pad-${i}` });
    for (let d = 1; d <= total; d++) cells.push({ date: new Date(cursor.getFullYear(), cursor.getMonth(), d), key: `d-${d}` });
    while (cells.length % 7 !== 0) cells.push({ date: null, key: `trail-${cells.length}` });

    const today = new Date();

    return (
        <div className="rounded-2xl bg-white border border-purple-100 p-4 shadow">
            <div className="flex items-center justify-between mb-2">
                <button onClick={() => setCursor(c => addMonths(c, -1))} className="p-1 rounded-full hover:bg-gray-100" aria-label="Forrige måned"><ChevronLeft className="w-4 h-4" /></button>
                <div className="text-sm font-semibold capitalize">{monthLabel}</div>
                <button onClick={() => setCursor(c => addMonths(c, 1))} className="p-1 rounded-full hover:bg-gray-100" aria-label="Neste måned"><ChevronRight className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-7 text-[11px] text-gray-600 mb-1">
                {['S', 'M', 'T', 'O', 'T', 'F', 'L'].map((w, i) => (<div key={i} className="py-1 text-center">{w}</div>))}
            </div>
            <div className="grid grid-cols-7 gap-1 text-sm">
                {cells.map(({ date, key }) => {
                    if (!date) return <div key={key} className="h-8" />;
                    const selected = isSameDay(date, value);
                    const isToday = isSameDay(date, today);
                    return (
                        <button key={key} onClick={() => onChange(date)} className={cls("h-8 rounded-md text-center", selected ? "bg-purple-600 text-white" : isToday ? "bg-purple-100 text-purple-800" : "hover:bg-gray-100")}>{date.getDate()}</button>
                    );
                })}
            </div>
        </div>
    );
}

/**********************
 * Hovedkomponent
 **********************/
type agentDashboardProps = {
    currentUser: any,
    onLogout: string,
    baseUrl: string,
}

let ringtoneAudio: HTMLAudioElement | null = null;

function AgentDashboard({ currentUser, onLogout, baseUrl }: agentDashboardProps) {

    // const { user, login, logout } = useAuth(); // ✅ eneste deklarasjon
    const client = useSelector((state: RootState) => state.client.client);
    const default_configs = client ? JSON.parse(client.default_configs || '{}') : {};
    const [advisorFlags, setAdvisorFlags] = useState(null);

    const [type, setType] = useState("audio");


    // incoming call modal
    const [showCallModal, setShowCallModal] = useState(false);
    const [showBroadcastModal, setShowBroadcastModal] = useState(false);
    const [callId, setCallId] = useState(null);
    const [callerName, setCallerName] = useState(null);

    // Stripe
    const [stripeExpressLink, setStripeMerchantLink] = useState(null);
    const [stripeLinked, setStripeLinked] = useState<boolean>(false);
    const [stripeAccountId, setStripeAccountId] = useState<string>('');

    function connectStripe() {
        window.open(stripeExpressLink ? stripeExpressLink : '', "_blank");
        // const id = (globalThis.crypto && "randomUUID" in globalThis.crypto)
        //     ? crypto.randomUUID().slice(0, 12)
        //     : "acct_" + Math.random().toString(36).slice(2, 10);
        // setStripeLinked(true);
        // setStripeAccountId(id);
        // try { localStorage.setItem("stripeLinked", "1"); localStorage.setItem("stripeAccountId", id); } catch { }
        // alert("Stripe‑konto tilkoblet (demo)");
    }

    // Agent data
    const fetchAgent = async () => {
        const response = await axios.get(baseUrl + "/getAgentByUID", {
            params: { token: currentUser.token },
        });
        if (response.data && response.data.status === "OK") {
            if (response.data.data.stripe_express_account_link) {
                setStripeMerchantLink(response.data.data.stripe_express_account_link);
                setStripeAccountId(response.data.data.stripe_id);
                setStripeLinked(true);
            }
            return response.data.data.uid;
        }
        throw new Error("Failed to fetch customer");
    };
    const { data: agent_uid } = useQuery({
        queryKey: ["agent", currentUser?.token],
        queryFn: fetchAgent,
        enabled: !!currentUser, // only run if logged in
    });


    // Tabs
    const [tab, setTab] = useState<
        "oversikt" | "avtaler" | "kalender" | "utbetalinger" | "meldinger" | "profil" | "innstillinger"
    >("oversikt");

    // Login state
    // const [loginName, setLoginName] = useState("");
    // const [loginPhone, setLoginPhone] = useState("");

    // Valgt dato + kolonnelås
    const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());
    const [lockToSelectedDay, setLockToSelectedDay] = useState<boolean>(() => {
        try { return localStorage.getItem('advisorLockCols') !== '0'; } catch { return true; }
    });



    const navigate = useNavigate();
    useEffect(() => {
        if (!currentUser || currentUser.role !== 'agent') {
            navigate('/'); // Redirect to home if not logged in or not an agent
        }
    }, [currentUser, navigate]);

    useEffect(() => { try { localStorage.setItem('advisorLockCols', lockToSelectedDay ? '1' : '0'); } catch { } }, [lockToSelectedDay]);

    // Slots
    const [slotMinutes, setSlotMinutes] = useState<number>(() => {
        try { return parseInt(localStorage.getItem("advisorSlotMinutes") || "30", 10); } catch { return 30; }
    });
    useEffect(() => { try { localStorage.setItem("advisorSlotMinutes", String(slotMinutes)); } catch { } }, [slotMinutes]);

    // Samtale-mottak toggles
    const [enableVideo, setEnableVideo] = useState<boolean>(false);
    const [enableIVR, setEnableIVR] = useState<boolean>(false);
    const [enableWEBrtc, setEnableWEBrtc] = useState<boolean>(false);
    const [minutePrice, setMinutePrice] = useState<number>(0);
    const [consultPrice, setConsultPrice] = useState<number>(0);
    const [consultDuration, setConsultDuration] = useState<number>(0);
    const getAdvisorFlags = async () => {
        var formData = new FormData();
        formData.append("token", currentUser?.token);
        const response = await axios.post(baseUrl + `/getAgentPermissionsAndPricing`, formData)
        setEnableIVR(response.data.data.enableIVR);
        setEnableVideo(response.data.data.enableVideo);
        setEnableWEBrtc(response.data.data.enableWEBrtc);
        setAdvisorFlags(response.data.data);
        setMinutePrice(response.data.customer_minute_price);
        setConsultPrice(response.data.minute_price_conference);
        localStorage.setItem('advisorFlags', JSON.stringify(response.data.data));
    }
    useEffect(() => {
        // try {

        //     localStorage.setItem('advisorFlags', JSON.stringify({ enableVideo, enableIVR, enableWEBrtc }));
        // }
        // catch { }
        getAdvisorFlags();
    }, []);

    // Prising (per minutt / per konsultasjon)
    type PricingMode = 'per_min' | 'per_consult';
    const [pricingMode, setPricingMode] = useState<PricingMode>(() => {
        try { return (localStorage.getItem('advisorPricingMode') as PricingMode) || 'per_min'; } catch { return 'per_min'; }
    });

    const handlePermissionsAndPrices = async () => {
        var formData = new FormData();
        formData.append("can_video_call", enableVideo.toString());
        formData.append("enableIVR", enableIVR.toString());
        formData.append("WebRTC", enableWEBrtc.toString());
        formData.append("pricingMode", pricingMode);
        formData.append("customer_minute_price", minutePrice.toString());
        formData.append("minute_price_conference", consultPrice.toString());
        formData.append("token", currentUser?.token);

        const response = await axios.post(baseUrl + `/updateAgentPermissionsAndPricing`, formData);

        console.log(response);
    }
    const handleIVRChange = () => {
        setEnableIVR(prev => {
            const newIVR = !prev;      // toggle IVR
            if (newIVR) setEnableWEBrtc(false); // only turn off WEBrtc if IVR is on
            return newIVR;
        });
    };

    const handleWebRTCChange = () => {
        setEnableWEBrtc(prev => {
            const newWebRTC = !prev;   // toggle WEBrtc
            if (newWebRTC) setEnableIVR(false); // only turn off IVR if WEBrtc is on
            return newWebRTC;
        });
    };
    // useEffect(() => { try { localStorage.setItem('advisorPricingMode', pricingMode); } catch { } }, [pricingMode]);
    // useEffect(() => { try { localStorage.setItem('advisorMinutePrice', String(minutePrice)); } catch { } }, [minutePrice]);
    // useEffect(() => { try { localStorage.setItem('advisorConsultPrice', String(consultPrice)); } catch { } }, [consultPrice]);
    // useEffect(() => { try { localStorage.setItem('advisorConsultDuration', String(consultDuration)); } catch { } }, [consultDuration]);




    // Vipps
    const [vippsLinked, setVippsLinked] = useState<boolean>(() => {
        try { return localStorage.getItem('vippsLinked') === '1'; } catch { return false; }
    });
    const [vippsMerchantId, setVippsMerchantId] = useState<string>(() => {
        try { return localStorage.getItem('vippsMerchantId') || ''; } catch { return ''; }
    });
    function connectVipps() {
        const id = 'MID' + Math.random().toString(36).slice(2, 8).toUpperCase();
        setVippsLinked(true);
        setVippsMerchantId(id);
        try { localStorage.setItem('vippsLinked', '1'); localStorage.setItem('vippsMerchantId', id); } catch { }
        alert('Vipps‑konto tilkoblet (demo)');
    }

    // Avtaler (mock)
    type Appt = { id: string; kunde: string; start: string; slutt: string; type: "Chat" | "Samtale" | "Video"; status: "Planlagt" | "Fullført" | "Avlyst"; belop: number };
    const [appts, setAppts] = useState<Appt[]>(() => [
        { id: Math.random().toString(36).slice(2), kunde: "Silje", start: todayISO() + "T10:00", slutt: todayISO() + "T10:30", type: "Chat", status: "Planlagt", belop: 249 },
        { id: Math.random().toString(36).slice(2), kunde: "Anders", start: todayISO() + "T12:00", slutt: todayISO() + "T12:20", type: "Samtale", status: "Planlagt", belop: 199 },
        { id: Math.random().toString(36).slice(2), kunde: "Ola", start: todayISO() + "T15:00", slutt: todayISO() + "T15:30", type: "Video", status: "Fullført", belop: 399 },
    ]);

    // Ny avtale (bruk pricing/slot)
    const [newName, setNewName] = useState("");
    const [newStart, setNewStart] = useState<string>(todayISO() + "T10:00");
    const [newType, setNewType] = useState<"Chat" | "Samtale" | "Video">("Chat");

    function addMinutesISO(iso: string, minutes: number) {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        d.setMinutes(d.getMinutes() + minutes);
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
    function addAppt() {
        if (!newName.trim()) return;
        const useMinutes = pricingMode === 'per_consult' ? consultDuration : slotMinutes;
        const start = newStart;
        const slutt = addMinutesISO(start, useMinutes);
        const id = Math.random().toString(36).slice(2);
        const belop = pricingMode === 'per_consult' ? consultPrice : minutePrice * useMinutes;
        setAppts(list => [{ id, kunde: newName.trim(), start, slutt, type: newType, status: "Planlagt", belop }, ...list]);
        setNewName("");
    }

    // Utbetalinger (mock)
    type Payout = { id: string; dato: string; belop: number; status: "Avventer" | "Utbetalt" };
    const [payouts] = useState<Payout[]>([
        { id: Math.random().toString(36).slice(2), dato: todayISO(), belop: 1299, status: "Avventer" },
        { id: Math.random().toString(36).slice(2), dato: todayISO(), belop: 899, status: "Utbetalt" },
    ]);

    // Tilgjengelighetskalender (state)
    type Avail = Record<(typeof DAYS)[number], Record<number, boolean>>;
    const [availability, setAvailability] = useState<Avail>(() => {
        const base: Avail = { Man: {}, Tir: {}, Ons: {}, Tor: {}, Fre: {}, Lør: {}, Søn: {} } as any;
        for (let d of DAYS) for (let h = 7; h <= 22; h++) (base as any)[d][h] = h >= 9 && h <= 16;
        try { const raw = localStorage.getItem('advisorAvail'); return raw ? JSON.parse(raw) : base; } catch { return base; }
    });
    useEffect(() => { try { localStorage.setItem('advisorAvail', JSON.stringify(availability)); } catch { } }, [availability]);

    // Meldinger (mock)
    type Thread = { id: string; with: string; msgs: Array<{ me: boolean; text: string; at: number }>; };
    const [threads, setThreads] = useState<Thread[]>([
        { id: Math.random().toString(36).slice(2), with: "Eirik", msgs: [{ me: false, text: "Hei – ledig kl 14?", at: Date.now() - 3600e3 }, { me: true, text: "Ja, holder av 30 min.", at: Date.now() - 3500e3 }] },
        { id: Math.random().toString(36).slice(2), with: "Linn", msgs: [{ me: false, text: "Har du tips til Navratri‑kampanje?", at: Date.now() - 7200e3 }] },
    ]);
    const [activeThread, setActiveThread] = useState<string>("");
    useEffect(() => { if (!activeThread && threads[0]) setActiveThread(threads[0].id); }, [activeThread, threads]);
    const active = useMemo(() => threads.find(t => t.id === activeThread) || threads[0], [threads, activeThread]);
    const [draft, setDraft] = useState("");
    function sendMsg() { if (!draft.trim() || !active) return; setThreads(list => list.map(t => t.id === active.id ? { ...t, msgs: [...t.msgs, { me: true, text: draft.trim(), at: Date.now() }] } : t)); setDraft(""); }

    // Oversikt‑summer
    const totaltAvventer = payouts.filter(p => p.status === "Avventer").reduce((s, p) => s + p.belop, 0);
    const totaltUtbetalt = payouts.filter(p => p.status === "Utbetalt").reduce((s, p) => s + p.belop, 0);

    // Login‑validering
    // const validLogin = loginName.trim().length > 1 && /\d{7,13}/.test(loginPhone.replace(/\D/g, ""));

    // Avtaler for valgt dag
    const selectedISO = ymdLocal(selectedDate);
    const apptsForSelected = useMemo(() => {
        return [...appts].filter(a => a.start?.slice(0, 10) === selectedISO).sort((a, b) => a.start?.localeCompare(b.start));
    }, [appts, selectedISO]);

    //Enabling sound
    const [enabledSound, setEnabledSound] = useState<boolean>(false);
    useEffect(() => {
        if (!enabledSound && default_configs.WebRTC === "yes") {
            Swal.fire({
                title: "🔔 Enable Sound",
                text: "Press Enable to receive call alerts with sound.",
                confirmButtonText: "Enable",
                allowOutsideClick: false,
            }).then(() => {
                setEnabledSound(true);
                ringtoneAudio = new Audio("/incoming-ringtone.mp3");
                ringtoneAudio.play().then(() => {
                    ringtoneAudio?.pause();
                });
            });
        }
    }, [default_configs]);

    // ——— SMOKE TESTS (ikke endre brukeratferd) ———
    useEffect(() => {
        try {
            const times = genTimes(7, 8, 60);
            console.assert(times.length === 1 && times[0] === '07:00', 'genTimes basic failed');
            const qtr = genTimes(7, 8, 15); // 15‑min steg
            console.assert(qtr.length === 4 && qtr[0] === '07:00' && qtr[3] === '07:45', 'genTimes 15‑min failed');
            const iso = '2025-01-01T10:00';
            const plus30 = (function add(iso: string, m: number) { const d = new Date(iso); d.setMinutes(d.getMinutes() + m); const p = (n: number) => String(n).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; })(iso, 30);
            console.assert(plus30.endsWith('10:30'), 'addMinutesISO smoke failed');
        } catch { }
    }, []);
    const [webSocketConn, setWebSocketConn] = useState<WebSocket | null>(null);
    const displayCallModal = (data: any) => {
        if (data.type === "video_call") {
            setType("video");
            if (data.about === "video_call") {
                setCallerName(data.information.customer_name);
                setShowCallModal(true);
            } else if (data.about === "hangup_by_customer") {
                setShowCallModal(false);
                stopRingtone();
            }
        } else if (data.type === "audio_call") {
            setType("audio");
            if (data.about === "callback_initated_call") {
                // $("#callbackCallModalText").html("Hi " + data.information.agent_name + ", You have a new callback request from " + data.information.customer_name + ". Check your callback list.");
                // $("#callbackCallModal").modal("toggle");
                // setTimeout(() => {
                //     $("#callbackCallModal").modal("hide");
                // }, 3000);
            } else if (data.about === "WebCall_initated_call") {
                setCallerName(data.information.customer_name);
                setShowCallModal(true);
            } else if (data.about === "WebCall_cancelled_by_customer") {
                setShowCallModal(false);
                stopRingtone();
            } else if (data.about === "SupportCall_agent_accepted_call") {
                // $("#WebCallWaitingModal").modal("hide");
                window.open("agent/one-to-one-support-call/" + data.call_history_id, "_blank");
            } else if (data.about === "SupportCall_agent_rejected_by_support") {
                // $(".WebCallWaitingModalBody").html(`<p class="col-12 text-center"><strong>Your call was declined</strong><br><br>It looks like the support agent is unavailable right now.<br>Please try again later.</p><button style="padding: 5px 15px;border-radius: 20px;" data-dismiss="modal" class="btn-danger">OK</button><br><br>`);
            }
        }
    }

    function playRingtone() {
        // If ringtone already playing, don’t restart it
        if (ringtoneAudio && !ringtoneAudio.paused) return;

        ringtoneAudio = new Audio("../../../incoming-ringtone.mp3");
        ringtoneAudio.loop = true;

        ringtoneAudio.play().catch((err) => {
            console.warn("Ringtone autoplay blocked:", err);
            // fallback: request user interaction or show notification
        });

        // Optional: also vibrate on mobile
        if (navigator.vibrate) {
            navigator.vibrate([200, 100, 200, 100, 200]);
        }
    }
    function stopRingtone() {
        document.title = "Chamcore MultiTheme1";
        if (ringtoneAudio) {
            ringtoneAudio.pause();
            ringtoneAudio.currentTime = 0; // rewind
            ringtoneAudio = null;
        }

        // stop vibration if any
        if (navigator.vibrate) {
            navigator.vibrate(0);
        }
    }
    const triggerIncomingCall = (data: any) => {
        if (!document.hidden) {
            displayCallModal(data);
            if (data.about === "WebCall_initated_call" || data.about === "video_call") {
                playRingtone();
            }
        } else {
            if (data.about === "WebCall_initated_call" || data.about === "video_call") {
                // tab not visible: show alert and play sound
                document.title = "📞 Incoming Call!";
                playRingtone();
                // optional vibration for mobile
                if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
            }
            displayCallModal(data);
        }
        setTimeout(() => {
            setShowCallModal(false);
            stopRingtone();
            let message = {};
            if (data.about === "WebCall_initated_call") {

                message = {
                    call_history_id: data.call_history_id,
                    about: "WebCall_timeout_by_agent",
                    channel: "common_channel",
                    type: "audio_call"
                };
            } else if (data.about === "video_call") {
                message = {
                    call_history_id: data.call_history_id,
                    about: "timeout_by_agent",
                    channel: "common_channel",
                    type: "video_call"
                }
            }
            if (webSocketConn?.readyState !== WebSocket.OPEN && message &&
                typeof message === "object" &&
                Object.keys(message).length > 0) {
                var wsServer = "";
                if (baseUrl.includes("localhost")) {
                    wsServer = "ws://localhost:8013/echo";
                } else {
                    wsServer = "wss://chamcore.com/echo";
                }
                const connection = new WebSocket(wsServer);
                setWebSocketConn(connection);
                connection.onopen = () => {
                    connection.send(JSON.stringify(message));
                }
            }
        }, 7000);
    }


    useEffect(() => {
        try {
            let data: any = "";

            const wsServer = baseUrl.includes("localhost")
                ? "ws://localhost:8013/echo"
                : "wss://chamcore.com/echo";

            const connection = new WebSocket(wsServer);
            connection.onopen = () => connection.send("Connection Established!");
            connection.onmessage = function (e) {
                if (e.data !== "" && e.data !== null) {
                    data = parseUntilObject(e.data);
                }
                // inside your agent WebSocket listener
                const channel = new BroadcastChannel("incoming_call_channel");
                if (data.information.agent_id === agent_uid) {
                    channel.postMessage(data);
                    setCallId(data.call_history_id);
                    triggerIncomingCall(data);
                }
            };
            setWebSocketConn(connection);

            // ✅ cleanup on unmount / re-run
            return () => {
                connection.close();
            };

        } catch (e) {
            console.log("WebSocket error:", e);
        }

    }, [agent_uid])
    
    if (!currentUser || currentUser.role !== 'agent') {
        return null; // render nothing while redirecting
    }
    // UI
    // if (!user) {
    //     return (
    //         <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-indigo-50 flex items-center justify-center px-4">
    //             <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow border border-purple-100">
    //                 <div className="flex items-center gap-2 text-purple-800 mb-3"><LogIn className="w-5 h-5" /><strong>Veileder‑innlogging</strong></div>
    //                 <label className="block text-sm text-gray-700">Navn</label>
    //                 <input value={loginName} onChange={e => setLoginName(e.target.value)} className="w-full rounded-xl border px-3 py-2 mb-3" placeholder="Fornavn Etternavn" />
    //                 <label className="block text-sm text-gray-700">Mobil</label>
    //                 <input value={loginPhone} onChange={e => setLoginPhone(e.target.value)} className="w-full rounded-xl border px-3 py-2" placeholder="Ditt nummer" />
    //                 <button disabled={!validLogin} onClick={() => login(loginName, loginPhone)} className={cls("mt-4 w-full rounded-full px-4 py-2 font-semibold", validLogin ? "bg-purple-600 text-white hover:bg-purple-700" : "bg-gray-200 text-gray-500")}>Logg inn</button>
    //                 <p className="text-xs text-gray-500 mt-3">Ingen passord i demo – kobles til ekte auth senere.</p>
    //             </div>
    //         </div>
    //     );
    // }



    return (
        <div className="min-h-screen bg-gray-50">
            {/* Topbar */}

            <AgentHeader currentUser={currentUser} setTab={setTab} onLogout={onLogout} />
            {/* <div className="bg-gradient-to-r from-fuchsia-500 via-purple-600 to-indigo-600 text-white">
                <div className="max-w-7xl mx-auto flex items-center justify-between p-3">
                    <div className="flex items-center gap-2"><User className="w-5 h-5" /><span className="font-semibold">{currentUser.name}</span></div>
                    <div className="flex items-center gap-4">
                        <button onClick={() => setTab("oversikt")} className="hover:underline">Oversikt</button>
                        <button onClick={() => setTab("avtaler")} className="hover:underline">Avtaler</button>
                        <button onClick={() => setTab("kalender")} className="hover:underline">Kalender</button>
                        <button onClick={() => setTab("utbetalinger")} className="hover:underline">Utbetalinger</button>
                        <button onClick={() => setTab("meldinger")} className="hover:underline">Meldinger</button>
                        <button onClick={() => setTab("profil")} className="hover:underline">Profil</button>
                        <button onClick={() => setTab("innstillinger")} className="hover:underline">Innstillinger</button>
                    </div>
                    <button onClick={onLogout} className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1"><LogOut className="w-4 h-4" /> Logg ut</button>
                </div>
            </div> */}

            <div className="max-w-7xl mx-auto p-4">
                {/* O V E R S I K T */}
                {tab === "oversikt" && (
                    <div className="grid gap-4 md:grid-cols-3">
                        <div className={card}>
                            <div className="text-sm text-gray-600">Avtaler i dag</div>
                            <div className="text-3xl font-extrabold text-purple-900">{3}</div>
                        </div>
                        <div className={card}>
                            <div className="text-sm text-gray-600">Avventer utbetaling</div>
                            <div className="text-3xl font-extrabold text-purple-900">{NOK(totaltAvventer)}</div>
                        </div>
                        <div className={card}>
                            <div className="text-sm text-gray-600">Utbetalt</div>
                            <div className="text-3xl font-extrabold text-purple-900">{NOK(totaltUtbetalt)}</div>
                        </div>

                        {/* Stripe + Vipps + mottak/prising */}
                        <div className="md:col-span-3 grid gap-4 md:grid-cols-2">
                            <div className={card}>
                                {default_configs?.card_payment === "yes" && (
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <h3 className="font-bold text-purple-900 flex items-center gap-2">
                                                <CreditCard className="w-4 h-4" /> Stripe Connect
                                            </h3>
                                            {stripeLinked ? (
                                                <span className="inline-flex items-center gap-1 text-green-700 text-sm">
                                                    <CheckCircle2 className="w-4 h-4" /> {stripeAccountId}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-amber-700 text-sm">
                                                    <AlertTriangle className="w-4 h-4" /> Ikke tilkoblet
                                                </span>
                                            )}
                                        </div>

                                        <p className="text-sm text-gray-600">
                                            Koble kontoen din for å motta utbetalinger automatisk.
                                        </p>

                                        <button
                                            onClick={connectStripe}
                                            className="rounded-full bg-purple-600 text-white px-4 py-2 hover:bg-purple-700 transition"
                                        >
                                            Stripe-konto
                                        </button>
                                    </div>
                                )}


                                {default_configs?.vipps_pay === "yes" ? (<div className="mt-4 border-t pt-4">
                                    <div className="flex items-center justify-between">
                                        <h4 className="font-semibold text-purple-900">Vipps</h4>
                                        {vippsLinked ? (
                                            <span className="inline-flex items-center gap-1 text-green-700 text-sm"><CheckCircle2 className="w-4 h-4" /> {vippsMerchantId}</span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-amber-700 text-sm"><AlertTriangle className="w-4 h-4" /> Ikke tilkoblet</span>
                                        )}
                                    </div>
                                    <button onClick={connectVipps} className="mt-2 rounded-full bg-orange-500 text-white px-4 py-2">Koble Vipps‑konto</button>
                                </div>) : ('')}

                            </div>

                            <div className={card}>
                                <div className="grid grid-cols-[1fr_auto_auto] gap-2 mb-3 mr-auto">
                                    <h3 className="font-bold text-purple-900">Samtalemottak & priser</h3>
                                    <button onClick={() => setShowBroadcastModal(true)} className='justify-self-end rounded-full bg-purple-600 text-white px-4 py-2'>Stream</button>
                                    <button onClick={handlePermissionsAndPrices} className='justify-self-end rounded-full bg-purple-600 text-white px-4 py-2'>Update</button>
                                </div>
                                <div className="grid md:grid-cols-3 gap-3 mb-3">
                                    <Toggle label="Video" checked={enableVideo} onChange={() => setEnableVideo(v => !v)} bordering={true} />
                                    <Toggle label="IVR" checked={enableIVR} onChange={handleIVRChange} bordering={true} />
                                    <Toggle label="WEBrtc" checked={enableWEBrtc} onChange={handleWebRTCChange} bordering={true} />
                                </div>
                                <div className="rounded-xl border p-3">
                                    <div className="flex flex-wrap items-center gap-4 text-sm mb-3">
                                        <label className="inline-flex items-center gap-2"><input type="radio" checked={pricingMode === 'per_min'} onChange={() => setPricingMode('per_min')} /> Per minutt</label>
                                        <label className="inline-flex items-center gap-2"><input type="radio" checked={pricingMode === 'per_consult'} onChange={() => setPricingMode('per_consult')} /> Per konsultasjon</label>
                                    </div>
                                    {pricingMode === 'per_min' ? (
                                        <div className="grid sm:grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs text-gray-600">Minuttpris (NOK)</label>
                                                <input type="number" min={0} value={minutePrice} onChange={e => setMinutePrice(Math.max(0, Number(e.target.value || 0)))} className="w-full rounded-lg border px-2 py-1" />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-600">Standard varighet (min)</label>
                                                <input type="number" min={5} step={5} value={slotMinutes} onChange={e => setSlotMinutes(Math.max(5, Number(e.target.value || 15)))} className="w-full rounded-lg border px-2 py-1" />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="grid sm:grid-cols-3 gap-3">
                                            <div>
                                                <label className="block text-xs text-gray-600">Pris per konsultasjon (NOK)</label>
                                                <input type="number" min={0} value={consultPrice} onChange={e => setConsultPrice(Math.max(0, Number(e.target.value || 0)))} className="w-full rounded-lg border px-2 py-1" />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-600">Varighet (min)</label>
                                                <input type="number" min={5} step={5} value={consultDuration} onChange={e => setConsultDuration(Math.max(5, Number(e.target.value || 15)))} className="w-full rounded-lg border px-2 py-1" />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-600">Start‑slot (min)</label>
                                                <input type="number" min={5} step={5} value={slotMinutes} onChange={e => setSlotMinutes(Math.max(5, Number(e.target.value || 15)))} className="w-full rounded-lg border px-2 py-1" />
                                            </div>
                                        </div>
                                    )}
                                    <p className="text-xs text-gray-500 mt-2">Innstillinger lagres lokalt og brukes ved nye avtaler.</p>
                                </div>
                            </div>
                            <div className={card}>
                                <PerformanceAndKpi />
                            </div>
                            <div className={card}>
                                <Invoices baseUrl={baseUrl} />
                            </div>
                        </div>
                    </div>
                )}

                {/* A V T A L E R */}
                {tab === "avtaler" && (
                    <div className={card}>
                        <Appointments newName={newName} setNewName={setNewName} newType={newType} setNewType={setNewType} newStart={newStart} setNewStart={setNewStart} pricingMode={pricingMode} consultDuration={consultDuration} slotMinutes={slotMinutes} consultPrice={consultPrice} minutePrice={minutePrice} addAppt={addAppt} NOK={NOK} appts={appts} setAppts={setAppts} currentUser={currentUser} baseUrl={baseUrl} />
                    </div>
                )}

                {/* K A L E N D E R */}
                {tab === "kalender" && (
                    <div className="grid gap-4 md:grid-cols-12">
                        {/* Venstre: hurtigpanel + slot-grid */}
                        <div className="md:col-span-9 rounded-2xl bg-white border border-purple-100 p-5 shadow">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="font-bold text-purple-900 flex items-center gap-2"><Calendar className="w-4 h-4" /> Tilgjengelighet</h3>
                                <label className="text-xs text-gray-600 inline-flex items-center gap-2">
                                    <input type="checkbox" checked={lockToSelectedDay} onChange={() => setLockToSelectedDay(v => !v)} />
                                    Lås kolonner til valgt dag ({WEEKDAY_NAMES[selectedDate.getDay()]})
                                </label>
                            </div>
                            <QuickPlanPanel availability={availability} setAvailability={setAvailability} slotMinutes={slotMinutes} setSlotMinutes={setSlotMinutes} />
                            <div className="overflow-auto mt-4">
                                <table className="min-w-[900px] text-sm">
                                    <thead>
                                        <tr>
                                            <th className="p-2 text-left text-gray-600">Tid</th>
                                            {DAYS.map(d => {
                                                const disabled = lockToSelectedDay && d !== WEEKDAY_NAMES[selectedDate.getDay()];
                                                return (
                                                    <th key={d} className={cls("p-2 text-gray-600", disabled && "opacity-40")}>{d}</th>
                                                );
                                            })}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {genTimes(7, 22, slotMinutes).map(t => (
                                            <tr key={t} className="border-t">
                                                <td className="p-2 text-gray-600">{t}</td>
                                                {DAYS.map(d => {
                                                    const hh = parseInt(t.slice(0, 2), 10);
                                                    const isOn = availability[d][hh];
                                                    const disabled = lockToSelectedDay && d !== WEEKDAY_NAMES[selectedDate.getDay()];
                                                    return (
                                                        <td key={d + "-" + t} className="p-1">
                                                            <button
                                                                disabled={disabled}
                                                                onClick={() => setAvailability(av => ({ ...av, [d]: { ...av[d], [hh]: !av[d][hh] } }))}
                                                                className={cls("w-full rounded-md py-2", disabled && "opacity-40 cursor-not-allowed", isOn ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-600")}
                                                            >{isOn ? "Ledig" : "Stengt"}</button>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <p className="text-xs text-gray-500 mt-3">Merk: Klikk på en rad toggler hele timen. Endringer lagres automatisk.</p>
                        </div>

                        {/* Høyre: mini månedskalender + dagens avtaler */}
                        <div className="md:col-span-3 space-y-3">
                            <MiniMonth value={selectedDate} onChange={setSelectedDate} />
                            <div className={card.replace("p-5", "p-4")}>
                                <div className="text-sm font-semibold mb-2">Avtaler {selectedISO}</div>
                                <ul className="space-y-2 text-sm">
                                    {apptsForSelected.length === 0 && (
                                        <li className="text-gray-500">Ingen avtaler denne dagen.</li>
                                    )}
                                    {apptsForSelected.map(a => (
                                        <li key={a.id} className="flex items-center justify-between border rounded-xl p-2">
                                            <span>{a.start?.slice(11, 16)} – {a.kunde}</span>
                                            <span className={cls("text-xs px-2 py-0.5 rounded-full",
                                                a.status === 'Planlagt' && 'bg-amber-100 text-amber-800',
                                                a.status === 'Fullført' && 'bg-green-100 text-green-800',
                                                a.status === 'Avlyst' && 'bg-red-100 text-red-800'
                                            )}>{a.status}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                {/* U T B E T A L I N G E R */}
                {tab === "utbetalinger" && (
                    <div className="grid gap-4 md:grid-cols-2">
                        <div className={card}>
                            <h3 className="font-bold text-purple-900 mb-3">Avventer utbetaling</h3>
                            <ul className="space-y-2 text-sm">
                                {payouts.filter(p => p.status === "Avventer").map(p => (
                                    <li key={p.id} className="flex items-center justify-between border rounded-xl p-2">
                                        <span>{p.dato}</span>
                                        <span className="font-semibold">{NOK(p.belop)}</span>
                                    </li>
                                ))}
                                {payouts.filter(p => p.status === "Avventer").length === 0 && <li className="text-gray-500">Ingen poster</li>}
                            </ul>
                        </div>
                        <div className={card}>
                            <h3 className="font-bold text-purple-900 mb-3">Utbetalt</h3>
                            <ul className="space-y-2 text-sm">
                                {payouts.filter(p => p.status === "Utbetalt").map(p => (
                                    <li key={p.id} className="flex items-center justify-between border rounded-xl p-2">
                                        <span>{p.dato}</span>
                                        <span className="font-semibold">{NOK(p.belop)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}

                {/* M E L D I N G E R */}
                {tab === "meldinger" && (
                    <div className="grid gap-4 md:grid-cols-3">
                        <div className={card.replace("p-5", "p-3")}>
                            <h4 className="font-semibold text-purple-900 mb-2">Samtaler</h4>
                            <ul className="space-y-1 text-sm">
                                {threads.map(t => (
                                    <li key={t.id}>
                                        <button onClick={() => setActiveThread(t.id)} className={cls("w-full text-left rounded-lg px-2 py-2", t.id === activeThread ? "bg-purple-100" : "hover:bg-gray-100")}>{t.with}</button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className={cls(card.replace("p-5", "p-3"), "md:col-span-2 flex flex-col")}>
                            <div className="flex-1 overflow-y-auto space-y-2">
                                {active?.msgs.map((m, i) => (
                                    <div key={i} className={cls("max-w-[70%] rounded-2xl px-3 py-2", m.me ? "ml-auto bg-purple-600 text-white" : "bg-gray-100 text-gray-800")}>{m.text}</div>
                                ))}
                            </div>
                            <div className="mt-3 flex gap-2">
                                <input value={draft} onChange={e => setDraft(e.target.value)} placeholder="Skriv en melding…" className="flex-1 rounded-xl border px-3 py-2" />
                                <button onClick={sendMsg} className="rounded-xl bg-purple-600 text-white px-4 py-2">Send</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* P R O F I L */}
                {tab === "profil" && (
                    <ProfileCard currentUser={currentUser} card={card} baseUrl={baseUrl} />
                    // <div className={card}>
                    //     <div className="grid gap-4 md:grid-cols-2">
                    //         <div>
                    //             <h3 className="font-bold text-purple-900 mb-2">Profil</h3>
                    //             <label className="text-sm text-gray-700">Visningsnavn</label>
                    //             <input className="w-full rounded-xl border px-3 py-2 mb-3" defaultValue={currentUser.name} />
                    //             <label className="text-sm text-gray-700">Tagline</label>
                    //             <input className="w-full rounded-xl border px-3 py-2 mb-3" placeholder="Kjærlighet, Tarot, Healing…" />
                    //             <label className="text-sm text-gray-700">Bio</label>
                    //             <textarea className="w-full rounded-xl border px-3 py-2" rows={5} placeholder="Skriv noen linjer om deg selv…" />
                    //         </div>
                    //         <div>
                    //             <h3 className="font-bold text-purple-900 mb-2">Priser</h3>
                    //             <div className="grid grid-cols-3 gap-3 text-sm">
                    //                 <div>
                    //                     <label>Chat / min</label>
                    //                     <input defaultValue={29} className="w-full rounded-xl border px-2 py-1" />
                    //                 </div>
                    //                 <div>
                    //                     <label>Samtale / min</label>
                    //                     <input defaultValue={39} className="w-full rounded-xl border px-2 py-1" />
                    //                 </div>
                    //                 <div>
                    //                     <label>Video / min</label>
                    //                     <input defaultValue={49} className="w-full rounded-xl border px-2 py-1" />
                    //                 </div>
                    //             </div>
                    //             <p className="text-xs text-gray-500 mt-2">Prisene kan vises offentlig i din profil.</p>
                    //         </div>
                    //     </div>
                    // </div>
                )}

                {/* I N N S T I L L I N G E R */}
                {tab === "innstillinger" && (
                    <div className={card}>
                        <h3 className="font-bold text-purple-900 mb-3 flex items-center gap-2"><Settings className="w-4 h-4" /> Innstillinger</h3>
                        <div className="grid md:grid-cols-2 gap-4 text-sm">
                            <div className="rounded-xl border p-3">
                                <h4 className="font-semibold mb-2">Notifikasjoner</h4>
                                <label className="flex items-center gap-2"><input type="checkbox" defaultChecked /> Push for nye avtaler</label>
                                <label className="flex items-center gap-2"><input type="checkbox" defaultChecked /> E‑post for utbetalinger</label>
                            </div>
                            <div className="rounded-xl border p-3">
                                <h4 className="font-semibold mb-2">Lenker</h4>
                                <div className="flex items-center gap-2 text-purple-700"><LinkIcon className="w-4 h-4" /> <a href="#">Hjelpesenter</a></div>
                                <div className="flex items-center gap-2 text-purple-700"><LinkIcon className="w-4 h-4" /> <a href="#">Retningslinjer</a></div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <CallModal
                show={showCallModal}
                onClose={() => setShowCallModal(false)}
                baseUrl={baseUrl}
                currentUser={currentUser}
                webSocketConn={webSocketConn}
                callerName={callerName}
                callId={callId}
                type={type}
                stopRingtone={stopRingtone}
            />
            <BroadcastModal
                show={showBroadcastModal}
                onClose={() => setShowBroadcastModal(false)}
                baseUrl={baseUrl}
                currentUser={currentUser}
            />
        </div>
    );
}


export default AgentDashboard;